import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { Box, Button, Divider, LinearProgress, Typography } from '@mui/material';
import { OfferAudioUploadState, OfferAudioVariantConfig } from '../types';

type AudioUploaderProps = {
    variant: OfferAudioVariantConfig;
    uploadState?: OfferAudioUploadState;
    canEdit: boolean;
    onFileUpload: (file: File) => void;
    stateKey: string;
    audioUrlError?: boolean;
};

function getDisplayFileName(audioFile: string): string {
    if (!audioFile) return '';
    try {
        const pathname = new URL(audioFile).pathname;
        if (/\.wav$/i.test(pathname)) {
            return pathname.split('/').pop() || audioFile;
        }
    } catch {
        // not a URL, return as-is
    }
    return audioFile;
}

export default function AudioUploader({ variant, uploadState, canEdit, onFileUpload, stateKey, audioUrlError }: AudioUploaderProps) {
    const resolvedAudioUrl = (variant.audioUrl || variant.audio_file || '').trim();

    return (
        <div className="oz-offer-language-config-upload-box">
            <div className="oz-offer-language-config-upload-actions">
                <Box>
                    <Typography sx={{ color: '#0f172a', fontSize: '13px', fontWeight: 500, lineHeight: '16px' }}>
                        Audio File Upload
                    </Typography>
                </Box>
                <Button
                    component="label"
                    variant="outlined"
                    startIcon={<CloudUploadOutlinedIcon />}
                    disabled={!canEdit || uploadState?.status === 'uploading'}
                    sx={{ borderRadius: '10px' }}
                >
                    Upload File
                    <input
                        hidden
                        type="file"
                        accept=".wav,audio/wav,audio/x-wav"
                        onChange={(event) => {
                            const file = event.target.files?.[0];
                            if (file) onFileUpload(file);
                            event.target.value = '';
                        }}
                    />
                </Button>
            </div>

            {variant.audio_file && (
                <Typography className="oz-offer-language-config-upload-file">
                    Current File: {getDisplayFileName(variant.audio_file)}
                </Typography>
            )}

            {uploadState?.status === 'uploading' && <LinearProgress sx={{ marginTop: '10px', borderRadius: '999px' }} />}

            {uploadState?.error && (
                <Typography sx={{ color: '#dc2626', fontSize: '12px', lineHeight: '16px', marginTop: '10px' }}>
                    {uploadState.error}
                </Typography>
            )}

            <div className="oz-offer-language-config-preview">
                <Typography sx={{ color: '#334155', fontSize: '12px', fontWeight: 500, lineHeight: '14px' }}>
                    Processed Audio URL
                </Typography>
                <Typography
                    sx={{
                        color: resolvedAudioUrl ? '#0f172a' : '#94a3b8',
                        fontSize: '13px',
                        lineHeight: '18px',
                        marginTop: '6px',
                        wordBreak: 'break-all',
                    }}
                >
                    {resolvedAudioUrl || 'Processed URL will appear here after upload.'}
                </Typography>
                {audioUrlError && (
                    <Typography sx={{ color: '#dc2626', fontSize: '12px', lineHeight: '16px', marginTop: '6px' }}>
                        Audio URL is required.
                    </Typography>
                )}

                {(uploadState?.previewUrl || resolvedAudioUrl) && (
                    <>
                        <Divider sx={{ marginY: '10px' }} />
                        <Typography sx={{ color: '#334155', fontSize: '12px', fontWeight: 500, lineHeight: '14px' }}>
                            Preview
                        </Typography>
                        <audio controls preload="none">
                            <source src={uploadState?.previewUrl || resolvedAudioUrl} />
                        </audio>
                    </>
                )}
            </div>
        </div>
    );
}
